import { useRef } from "react";
import CountUp from "react-countup";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Heart, Clock, Rocket, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTimelineRowMotion } from "@/hooks/useTimelineRowMotion";

type StatKey = "satisfaction" | "years" | "projects" | "clients";

interface StatDef {
    num: string;
    key: StatKey;
    end: number;
    suffix: string;
    icon: React.ReactNode;
    accentHue: number;
}

const STATS: StatDef[] = [
    { num: "01", key: "satisfaction", end: 99, suffix: "%", icon: <Heart className="w-8 h-8" />, accentHue: 260 },
    { num: "02", key: "years", end: 7, suffix: "+", icon: <Clock className="w-8 h-8" />, accentHue: 190 },
    { num: "03", key: "projects", end: 45, suffix: "+", icon: <Rocket className="w-8 h-8" />, accentHue: 320 },
    { num: "04", key: "clients", end: 102, suffix: "+", icon: <Users className="w-8 h-8" />, accentHue: 160 },
];

const StatCard = ({ stat, inView }: { stat: StatDef; inView: boolean }) => {
    const { t } = useTranslation();
    const accent = `hsl(${stat.accentHue} 90% 60%)`;
    const accentSoft = `hsl(${stat.accentHue} 90% 60% / 0.18)`;

    return (
        <div
            className="glass-v2 hover-lift-v2 rounded-[32px] p-8 md:p-10 group relative overflow-hidden"
            data-cursor="hover"
        >
            {/* Hover radial glow, stat-tinted */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `radial-gradient(500px circle at 50% 0%, ${accentSoft}, transparent 65%)`,
                }}
            />

            {/* Top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-40 pointer-events-none"
                style={{
                    background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                }}
            />

            <div className="relative">
                <div className="flex items-start gap-5 mb-6">
                    <div
                        className="w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                        style={{
                            background: `linear-gradient(145deg, ${accent}, hsl(${stat.accentHue} 90% 45%))`,
                            boxShadow: `0 0 32px ${accentSoft}`,
                        }}
                    >
                        {stat.icon}
                    </div>

                    <div className="flex-1 min-w-0 pt-1">
                        <p className="font-v2-label text-[10px] text-white/40 mb-1.5">
                            {t("stats_strip.eyebrow")} · {stat.num}
                        </p>
                        <p className="font-v2-display text-white text-5xl md:text-6xl leading-none">
                            {inView ? (
                                <CountUp end={stat.end} duration={1.8} useEasing suffix={stat.suffix} />
                            ) : (
                                <span>0{stat.suffix}</span>
                            )}
                        </p>
                    </div>
                </div>

                <h3 className="font-v2-display text-xl md:text-2xl text-white leading-tight mb-2">
                    {t(`stats_strip.${stat.key}`)}
                </h3>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-medium">
                    {t(`stats_strip.${stat.key}_caption`)}
                </p>
            </div>
        </div>
    );
};

const StatRow = ({ stat, index }: { stat: StatDef; index: number }) => {
    const isEven = index % 2 === 0;
    const accent = `hsl(${stat.accentHue} 90% 60%)`;
    const rowRef = useRef<HTMLLIElement>(null);
    const inView = useInView(rowRef, { once: true, margin: "-80px" });
    const { y, x, scale } = useTimelineRowMotion(rowRef, isEven);

    return (
        <motion.li
            ref={rowRef}
            style={{ y, x, scale }}
            className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-center"
        >
            {/* Center node on spine */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-14 h-14 rounded-full items-center justify-center z-10"
                style={{
                    background: "var(--gradient-accent-v2)",
                    boxShadow: `0 0 32px ${accent}, 0 0 60px hsl(260 90% 65% / 0.3)`,
                }}
            >
                <span className="font-v2-label text-[11px] text-white">{stat.num}</span>
            </div>

            {/* Connector stub from spine toward the card */}
            <div
                aria-hidden
                className={`hidden md:block absolute top-[66px] h-px w-10 lg:w-14 ${isEven ? "right-1/2 mr-7" : "left-1/2 ml-7"}`}
                style={{
                    background: `linear-gradient(${isEven ? "270deg" : "90deg"}, ${accent}, transparent)`,
                    opacity: 0.6,
                }}
            />

            <div className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`}>
                <StatCard stat={stat} inView={inView} />
            </div>
        </motion.li>
    );
};

const StatsStrip = () => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    return (
        <section ref={containerRef} className="py-20 md:py-32 relative">
            <div className="container mx-auto px-6 relative">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">{t("stats_strip.eyebrow")}</p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("stats_strip.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("stats_strip.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("stats_strip.subtitle")}
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive violet→cyan fill on scroll */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-16">
                        {STATS.map((s, i) => (
                            <StatRow key={s.key} stat={s} index={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default StatsStrip;
